// External Library Imports
import React, { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// React-Bootstrap Component Imports
import { Row, Col, Collapse, Button } from "react-bootstrap";

// Local Component Imports
import SearchSortDropdown from "../../../SearchSort/SearchSortDropdown";
import DistanceFilter from "../../../Filters/DistanceFilter/DistanceFilter";
import PriceFilter from "../../../Filters/PriceFilter/PriceFilter";
import AmenitiesFilters from "../../../Filters/AmenitiesFilter/AmenitiesFilter";
import BrandsFilters from "../../../Filters/BrandsFilter/BrandsFilter";

// Type, Interface, and Styled Component Imports
import { MobileFilterToggle } from "../../SearchResultHelpers";

interface SearchFilterProps {
  search: any;
  handlePriceChange: (priceFilter: string) => void;
  handleDistanceChange: (distanceFilter: number) => void;
  handleAmenitiesChange: (amenity: any, method: string) => void;
  handleBrandsChange: (brand: any, method: string) => void;
  handleSortChange: (sortBy: string, sortOrder: string) => void;
  crsCodesString: string;
  crsCodesBrandsString: string | null;
  hotelSearchFilters: string[];
  distanceRadius: number;
  brandsInUrl: any;
  priceCount: number;
  hotelCount: number;
  display: string;
  sortBy: string;
  sortOrder: string;
  background: string | undefined;
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  search,
  handlePriceChange,
  handleDistanceChange,
  handleAmenitiesChange,
  handleBrandsChange,
  handleSortChange,
  crsCodesString,
  crsCodesBrandsString,
  hotelSearchFilters,
  distanceRadius,
  brandsInUrl,
  priceCount,
  hotelCount,
  display,
  sortBy,
  sortOrder,
  background,
}) => {
  const { priceFilter, amenities, brands } = search;
  const showPriceFilter = hotelSearchFilters.indexOf("Price") > -1;
  const showDistanceFilter = hotelSearchFilters.indexOf("Distance") > -1;
  const showAmenitiesFilter = true;
  const showBrandFilter = hotelSearchFilters.indexOf("Brand") > -1;
  const [openFilter, setOpenFilter] = useState(false);

  const handleTogleMobileFilter = useCallback(() => {
    setOpenFilter(!openFilter);
  }, [openFilter]);

  return (
    <>
      <div className="px-2">
        <MobileFilterToggle
          onClick={handleTogleMobileFilter}
          openFilter={openFilter}
        >
          Filter & Sort
          <FontAwesomeIcon icon={openFilter ? "chevron-up" : "chevron-down"} />
        </MobileFilterToggle>
      </div>
      <Collapse in={openFilter} className="d-lg-block">
        <div
          id="searchFilters"
          className="search-filters-container mb-4"
          style={{ backgroundColor: background || "#f8f8f8" }}
        >
          <Row className="search-filters g-0" id="search-filters">
            <Col
              lg={{ span: 3 }}
              className={`text-left mt-3 mt-lg-0 ${openFilter && "mb-4"}`}
            >
              {priceCount > 0 && priceCount === hotelCount && (
                <SearchSortDropdown
                  display={display}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  handleSortChange={handleSortChange}
                />
              )}
            </Col>
            <Col lg={{ span: 9 }}>
              <Row className="justify-content-end">
                {showDistanceFilter && (
                  <Col
                    xs={12}
                    lg={3}
                    className={`pe-lg-0 mb-3 mb-lg-0 ${
                      showDistanceFilter ? "d-block" : "d-none"
                    }`}
                  >
                    <DistanceFilter
                      distance={distanceRadius}
                      handleDistanceChange={handleDistanceChange}
                    />
                  </Col>
                )}
                {showPriceFilter && (
                  <Col
                    xs={6}
                    lg={3}
                    className={`pe-lg-0 mb-3 mb-lg-0 ps-0 ps-lg-3 ${
                      showPriceFilter ? "d-block" : "d-none"
                    }`}
                  >
                    <PriceFilter
                      priceFilter={priceFilter}
                      handlePriceChange={handlePriceChange}
                    />
                  </Col>
                )}
                {process.env.GATSBY_AMENITIES_FILTER === "true" &&
                  showAmenitiesFilter && (
                    <Col xs={12} lg={3} className={`pe-lg-0 mb-3 mb-lg-0`}>
                      <AmenitiesFilters
                        crsCodes={crsCodesString}
                        handleAmenitiesChange={handleAmenitiesChange}
                        amenities={amenities}
                      />
                    </Col>
                  )}

                {showBrandFilter && (
                  <Col
                    xs={12}
                    lg={3}
                    className={showBrandFilter ? "d-block" : "d-none"}
                  >
                    <BrandsFilters
                      handleBrandsChange={handleBrandsChange}
                      brands={brands}
                      crsCodes={crsCodesString}
                      crsCodesBrands={crsCodesBrandsString}
                      brandsInUrl={brandsInUrl}
                    />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Button
            className="close-filters w-100 d-lg-none mt-3"
            variant="secondary"
            onClick={handleTogleMobileFilter}
          >
            Close
          </Button>
        </div>
      </Collapse>
    </>
  );
};

export default SearchFilter;
